import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, X, Save } from 'lucide-react';

/**
 * Modal editor for a single subtitle track. Users edit only the cue text —
 * timings, cue IDs and the header block are preserved verbatim by the backend.
 */
const SubtitleEditor = ({ title, getUrl, putUrl, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cues, setCues] = useState([]);
  const [initialTexts, setInitialTexts] = useState({});
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(getUrl, { withCredentials: true });
        if (cancelled) return;
        setCues(res.data.cues || []);
        const map = {};
        (res.data.cues || []).forEach(c => { map[c.index] = c.text; });
        setInitialTexts(map);
      } catch (err) {
        toast.error(err.response?.data?.detail || err.message || 'Failed to load subtitle');
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getUrl, onClose]);

  const changedCues = useMemo(
    () => cues.filter(c => c.text !== initialTexts[c.index]),
    [cues, initialTexts]
  );

  const filteredCues = useMemo(() => {
    if (!filter.trim()) return cues;
    const q = filter.toLowerCase();
    return cues.filter(c =>
      (c.text || '').toLowerCase().includes(q) ||
      (c.timing || '').toLowerCase().includes(q)
    );
  }, [cues, filter]);

  const handleTextChange = (index, value) => {
    setCues(prev => prev.map(c => c.index === index ? { ...c, text: value } : c));
  };

  const handleSave = async () => {
    if (changedCues.length === 0) {
      toast.info('No changes to save');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        putUrl,
        { cues: changedCues.map(c => ({ index: c.index, text: c.text })) },
        { withCredentials: true }
      );
      toast.success(`Saved ${changedCues.length} edited cue${changedCues.length === 1 ? '' : 's'}`);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" data-testid="subtitle-editor">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-semibold text-slate-900" data-testid="subtitle-editor-title">
              Edit Subtitle — {title}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Editing cue text only. Timings and cue IDs remain unchanged.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="subtitle-editor-close"
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100">
          <input
            type="text"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            data-testid="subtitle-editor-filter"
            placeholder="Filter cues by text or timestamp…"
            className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading cues…
            </div>
          ) : filteredCues.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">
              {cues.length === 0 ? 'This subtitle file has no cues.' : 'No cues match your filter.'}
            </p>
          ) : (
            <ul className="space-y-2">
              {filteredCues.map(cue => {
                const changed = cue.text !== initialTexts[cue.index];
                return (
                  <li
                    key={cue.index}
                    data-testid={`subtitle-cue-${cue.index}`}
                    className={`p-3 rounded-lg border transition-colors ${
                      changed ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-100'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-mono text-xs text-slate-500">
                        #{cue.index + 1} · {cue.timing}
                      </span>
                      {changed && (
                        <span className="text-xs text-amber-600 font-medium">edited</span>
                      )}
                    </div>
                    <textarea
                      value={cue.text}
                      onChange={e => handleTextChange(cue.index, e.target.value)}
                      data-testid={`subtitle-cue-text-${cue.index}`}
                      rows={Math.max(1, (cue.text || '').split('\n').length)}
                      className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 resize-y bg-white"
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50">
          <span className="text-xs text-slate-500">
            {changedCues.length === 0
              ? `${cues.length} cue${cues.length === 1 ? '' : 's'}`
              : `${changedCues.length} edited of ${cues.length}`}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={saving}
              data-testid="subtitle-editor-cancel"
              className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading || changedCues.length === 0}
              data-testid="subtitle-editor-save"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubtitleEditor;
